import {customElement, property} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '#lit/types/classes.js';
import type {FetchLogsHandler, InspectorLogs, Model} from './architecture.js';
import {main, ContentState, Msg} from './architecture.js';
import styles from './style.scss';
import tableStyles from '#lit/components/base/table/style.scss';

const LOGS_TABLE_ELEMENT_TAG = 'logs-inspector-table';

/**
 * A component for displaying log details in a table.
 */
@customElement(LOGS_TABLE_ELEMENT_TAG)
export default class LogsInspectorTable extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  static get styles() {
    return [styles, tableStyles];
  }

  /**
   * Logs to be displayed in the table.
   *
   * Note: If fetchLogsHandler is set, logs will be overridden by the response
   * from the fetchLogsHandler function.
   *
   * @param {InspectorLogs} logs - an array of {@link InspectorLogs} to be
   * displayed in the table.
   * @returns {void}
   */
  @property({type: Array})
  get logs(): InspectorLogs {
    return this.model.contents.kind === ContentState.RESOLVED
      ? this.model.contents.logs
      : [];
  }

  set logs(logs: InspectorLogs) {
    this.handleUpdate(Msg.setLogs(logs));
  }

  /**
   * Handler for fetching logs and converting them into an
   * {@link InspectorLogs} array to be displayed in the table.
   *
   * @param {FetchLogsHandler} handler - an async function that returns an
   * {@link InspectorLogs} array.
   * @returns {void}
   */
  @property({type: Object})
  get fetchLogsHandler(): FetchLogsHandler | undefined {
    return this.model.fetchLogsHandler;
  }

  set fetchLogsHandler(handler: FetchLogsHandler | undefined) {
    this.handleUpdate(Msg.setFetchLogsHandler(handler));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [LOGS_TABLE_ELEMENT_TAG]: LogsInspectorTable;
  }
}
